import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Repeat, Pencil, Sparkles, BookOpen } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const AddExpenseDialog = ({ open, onOpenChange, formData, setFormData, categories, onSubmit, businessId, token }) => {
  const [suggestion, setSuggestion] = useState(null);
  const [suggestTimeout, setSuggestTimeout] = useState(null);

  const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  // Debounced vendor suggestion
  useEffect(() => {
    if (suggestTimeout) clearTimeout(suggestTimeout);
    const vendor = formData.vendor?.trim();
    if (!vendor || vendor.length < 2 || !businessId) {
      setSuggestion(null);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/mybookie/vendor-suggest/${businessId}?vendor=${encodeURIComponent(vendor)}`, authHeaders);
        if (res.data.suggestion) {
          setSuggestion(res.data);
        } else {
          setSuggestion(null);
        }
      } catch (err) {
        console.error("Vendor suggestion error:", err);
        setSuggestion(null);
      }
    }, 400);
    setSuggestTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [formData.vendor, businessId]);

  const applySuggestion = () => {
    if (!suggestion) return;
    const matchedCat = categories.expense_categories?.find(
      c => c.name.toLowerCase() === suggestion.suggestion.toLowerCase()
    );
    if (matchedCat) {
      setFormData({ ...formData, category_id: matchedCat.id });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { onOpenChange(val); if (!val) setSuggestion(null); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Expense</DialogTitle>
          <DialogDescription>Record a new business expense.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Vendor (optional)</Label>
            <Input
              value={formData.vendor || ''}
              onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
              placeholder="Company or person paid"
              data-testid="add-expense-vendor"
            />
            {suggestion && (
              <div className="mt-1.5 flex items-center gap-2" data-testid="vendor-suggestion">
                <Badge
                  className={`text-xs cursor-pointer ${suggestion.source === 'learned' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 'bg-[#E2ECE7] text-[#0A2B1D] hover:bg-[#D0E0D8]'}`}
                  onClick={applySuggestion}
                >
                  {suggestion.source === 'learned' ? (
                    <><BookOpen className="w-3 h-3 mr-1 inline" /> Learned: {suggestion.suggestion}</>
                  ) : (
                    <><Sparkles className="w-3 h-3 mr-1 inline" /> Suggested: {suggestion.suggestion}</>
                  )}
                </Badge>
                <span className="text-xs text-[#8A948F]">Click to apply</span>
              </div>
            )}
          </div>
          <div>
            <Label>Category</Label>
            <Select value={formData.category_id || ''} onValueChange={(val) => setFormData({ ...formData, category_id: val })}>
              <SelectTrigger data-testid="add-expense-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.expense_categories?.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount ($)</Label>
            <Input type="number" step="0.01" value={formData.amount || ''} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })} placeholder="0.00" data-testid="add-expense-amount" />
          </div>
          <div>
            <Label>Date</Label>
            <Input type="date" value={formData.date || ''} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="add-expense-date" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="What was this expense for?" data-testid="add-expense-description" />
          </div>
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-[#0A2B1D]" />
                <Label className="cursor-pointer">Recurring Expense</Label>
              </div>
              <input type="checkbox" checked={formData.is_recurring || false} onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })} className="w-5 h-5 accent-indigo-600" />
            </div>
            {formData.is_recurring && (
              <div className="mt-3">
                <Label className="text-sm text-gray-600">Frequency</Label>
                <Select value={formData.recurring_frequency || 'monthly'} onValueChange={(val) => setFormData({ ...formData, recurring_frequency: val })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select frequency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setFormData({}); setSuggestion(null); }}>Cancel</Button>
          <Button onClick={onSubmit} disabled={!formData.category_id || !formData.amount || !formData.date || !formData.description} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="submit-expense-btn">Add Expense</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AddIncomeDialog = ({ open, onOpenChange, formData, setFormData, categories, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Add Income</DialogTitle>
        <DialogDescription>Record a new income entry.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Category</Label>
          <Select value={formData.category_id || ''} onValueChange={(val) => setFormData({...formData, category_id: val})}>
            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {categories.income_categories?.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Amount ($)</Label>
          <Input type="number" step="0.01" value={formData.amount || ''} onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})} placeholder="0.00" />
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={formData.date || ''} onChange={(e) => setFormData({...formData, date: e.target.value})} />
        </div>
        <div>
          <Label>Description</Label>
          <Input value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Source of income" />
        </div>
        <div>
          <Label>Source (optional)</Label>
          <Input value={formData.source || ''} onChange={(e) => setFormData({...formData, source: e.target.value})} placeholder="Customer or platform" />
        </div>
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Repeat className="w-4 h-4 text-green-600" />
              <Label className="cursor-pointer">Recurring Income</Label>
            </div>
            <input type="checkbox" checked={formData.is_recurring || false} onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})} className="w-5 h-5 accent-green-600" />
          </div>
          {formData.is_recurring && (
            <div className="mt-3">
              <Label className="text-sm text-gray-600">Frequency</Label>
              <Select value={formData.recurring_frequency || 'monthly'} onValueChange={(val) => setFormData({...formData, recurring_frequency: val})}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select frequency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => { onOpenChange(false); setFormData({}); }}>Cancel</Button>
        <Button onClick={onSubmit} disabled={!formData.category_id || !formData.amount || !formData.date || !formData.description} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm">Add Income</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const EditTransactionDialog = ({ editingItem, setEditingItem, editType, editForm, setEditForm, onSave, categories, businessId, token, onCategoriesChanged }) => {
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  const NEW_CATEGORY_SENTINEL = '__add_new__';

  const handleCategoryChange = (val) => {
    if (val === NEW_CATEGORY_SENTINEL) {
      setCreatingCategory(true);
      setNewCategoryName('');
    } else {
      setEditForm({ ...editForm, category_id: val });
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name || !businessId) return;
    setSavingCategory(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/categories`, {
        business_id: businessId,
        name,
        type: editType,   // 'expense' or 'income'
      }, authHeaders);
      const newCat = res.data;
      // Refresh parent's categories cache, then select the new one
      if (typeof onCategoriesChanged === 'function') {
        await onCategoriesChanged();
      }
      setEditForm({ ...editForm, category_id: newCat.id });
      setCreatingCategory(false);
      setNewCategoryName('');
    } catch (_e) {
      // Non-blocking — leave dialog open so user can retry
    } finally {
      setSavingCategory(false);
    }
  };

  const cats = editType === 'expense' ? categories?.expense_categories : categories?.income_categories;
  const showCategoryField = editType === 'expense' || editType === 'income';

  return (
    <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
      <DialogContent data-testid="edit-transaction-dialog">
        <DialogHeader>
          <DialogTitle>Edit {editType === 'expense' ? 'Expense' : 'Income'}</DialogTitle>
          <DialogDescription>
            {editType === 'expense' ? 'Update expense details. Changing the category for a vendor will teach the AI for future auto-categorization.' : 'Update income details.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {showCategoryField && cats && (
            <div>
              <label className="text-sm font-medium text-[#111614]">Category</label>
              {creatingCategory ? (
                <div className="flex items-center gap-2 mt-1" data-testid="new-category-inline">
                  <Input
                    autoFocus
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New category name"
                    className="flex-1"
                    data-testid="new-category-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); }
                      if (e.key === 'Escape') { setCreatingCategory(false); setNewCategoryName(''); }
                    }}
                  />
                  <Button size="sm" onClick={handleCreateCategory} disabled={!newCategoryName.trim() || savingCategory} data-testid="save-new-category-btn" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm">
                    {savingCategory ? 'Saving…' : 'Add'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setCreatingCategory(false); setNewCategoryName(''); }} className="rounded-sm">Cancel</Button>
                </div>
              ) : (
                <Select value={editForm.category_id || ''} onValueChange={handleCategoryChange}>
                  <SelectTrigger data-testid="edit-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    <SelectItem value={NEW_CATEGORY_SENTINEL} className="text-[#0A2B1D] font-medium" data-testid="add-new-category-option">
                      + Add new category…
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
              {editType === 'expense' && editForm.vendor && (
                <p className="text-xs text-[#8A948F] mt-1">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  Changing the category will teach the AI to use this category for &ldquo;{editForm.vendor}&rdquo; in the future
                </p>
              )}
            </div>
          )}
        <div>
          <label className="text-sm font-medium text-[#111614]">Amount ($)</label>
          <Input type="number" step="0.01" value={editForm.amount || ''} onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })} data-testid="edit-amount" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111614]">Description</label>
          <Input value={editForm.description || ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="edit-description" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#111614]">Date</label>
          <Input type="date" value={editForm.date || ''} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-date" />
        </div>
        {editType === 'expense' ? (
          <div>
            <label className="text-sm font-medium text-[#111614]">Vendor</label>
            <Input value={editForm.vendor || ''} onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })} data-testid="edit-vendor" />
          </div>
        ) : (
          <div>
            <label className="text-sm font-medium text-[#111614]">Source</label>
            <Input value={editForm.source || ''} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })} data-testid="edit-source" />
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-[#111614]">Notes</label>
          <Textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-notes" rows={2} />
        </div>
      </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditingItem(null)} className="rounded-sm">Cancel</Button>
          <Button onClick={onSave} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" data-testid="save-edit-btn">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
